import { type FC } from 'react'

import { Text } from '@wartek-id/text-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Button } from '@wartek-id/button-v1'

import { Header } from 'app/KinerjaShared/components/Header'
import {
  TableBody,
  TableHeader,
  TableHeaderItem,
  TablePelaksanaanKinerja,
  TableRow,
  TableRowItem,
  TableShimmer,
} from 'app/KinerjaShared/components/TablePelaksanaanKinerja'
import TableRowStates from 'app/KinerjaShared/components/TableRowStates'
import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'
import useTabKsGuru from 'app/KinerjaShared/hooks/useTabKsGuru'
import usePenetapanPredikatKinerjaPs from 'app/KinerjaPSPage/hooks/usePenetapanPredikatKinerjaPs'

type PredikatKinerjaProps = {}

const PredikatKinerja: FC<PredikatKinerjaProps> = ({}) => {
  const {
    isSuccess: isSuccessProfile,
    isPengawasSekolahOnly,
    leave,
  } = useRoleContext()

  if (isSuccessProfile && !isPengawasSekolahOnly) leave()

  // Tabs
  const {
    render: tabs,
    activeTabName,
    setCounters: setTabCounters,
  } = useTabKsGuru({
    // Initial counters, will be updated after fetching data. Required, to display 0 when currently fetching data.
    initCounters: { KS: 0, GURU: 0 },
  })

  const {
    // Kepala Sekolah
    itemsCountKs,
    refetchListKs,

    // Guru
    itemsCountGuru,
    refetchListGuru,

    // Combined
    isListLoading,
    isListBothFetched,
    hasItems,

    // Items
    relativeItems,
    isRelativeError,
    isRelativeSuccess,
  } = usePenetapanPredikatKinerjaPs({
    activeTabName,
    state: 'DINAS_TO_KS',
    confirmedOnly: true,
    onBothFetched: () => {
      // Set counters for tabs
      setTabCounters({ KS: itemsCountKs, GURU: itemsCountGuru })
    },
  })

  return (
    <>
      <Header title="Penetapan Predikat Kinerja" className="mb-9 print:mb-0" />

      <section className="bg-white rounded-lg">
        <div className="px-6 pt-6 flex items-start gap-4 print:p-0">
          {/* Title */}
          <div className="space-y-2.5 flex-1">
            <Text variant="heading-md">
              Berikut data dari Dinas Pendidikan:
            </Text>
            <Text color="subdued">
              Predikat Kinerja sudah dikirim ke Pegawai dan tidak dapat diubah
              lagi. Data mungkin muncul secara berkala.
            </Text>
          </div>

          <div className="flex-none flex flex-row gap-2.5 print:!hidden">
            {isListBothFetched && (
              <>
                <Button
                  size="lg"
                  color="blue"
                  type="button"
                  className="space-x-1"
                  variant="ghost"
                  onClick={() => window.print()}
                >
                  <Icon color="inherit">print_outlined</Icon> <span>Cetak</span>
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        {tabs}

        {/* Table */}
        <TablePelaksanaanKinerja className="mt-0">
          <TableHeader>
            <TableHeaderItem className="w-2/12">
              <Text variant="body" color="default" className="!font-bold">
                Nama
              </Text>
            </TableHeaderItem>
            <TableHeaderItem className="pl-0 w-2/12">
              <Text variant="body" color="default" className="!font-bold">
                Jabatan
              </Text>
            </TableHeaderItem>
            <TableHeaderItem className="pl-0 w-2/12">
              <Text variant="body" color="default" className="!font-bold">
                Satuan Pendidikan
              </Text>
            </TableHeaderItem>
            <TableHeaderItem className="pl-0 w-2/12">
              <Text variant="body" color="default" className="!font-bold">
                Predikat Kinerja
              </Text>
            </TableHeaderItem>
            <TableHeaderItem className="min-w-[120px] w-2/12 print:hidden" />
          </TableHeader>

          <TableBody>
            {/* If loading */}
            {isListLoading && <TableShimmer columns={4} />}

            {/* If error or no data returned */}
            {!isListLoading &&
              (isRelativeError || relativeItems?.length === 0) && (
                <TableRowStates
                  colSpan={6}
                  type={isRelativeError ? 'ERROR' : 'EMPTY'}
                  onClick={() => {
                    activeTabName === 'GURU'
                      ? refetchListGuru()
                      : refetchListKs()
                  }}
                >
                  Pastikan terdapat pegawai dengan jabatan tersebut di daerah
                  <br />
                  Anda dan sudah menyelesaikan tahap Pelaksanaan Kinerja.
                </TableRowStates>
              )}

            {!isListLoading &&
              isRelativeSuccess &&
              hasItems &&
              relativeItems.map((item, idx) => {
                return (
                  <TableRow key={`ks_${item.npsn}_${idx}`}>
                    <TableRowItem className="overflow-wrap-anywhere align-middle">
                      <Text variant="body-sm" color="default">
                        {item?.name || ''}
                      </Text>
                    </TableRowItem>
                    <TableRowItem className="overflow-wrap-anywhere align-middle pl-0">
                      <Text variant="body-sm" color="default">
                        {item?.jabatan || ''}
                      </Text>
                    </TableRowItem>
                    <TableRowItem className="overflow-wrap-anywhere align-middle pl-0">
                      <Text variant="body-sm" color="default">
                        {item?.satuanPendidikan || ''}
                      </Text>
                    </TableRowItem>
                    <TableRowItem className="overflow-wrap-anywhere align-middle pl-0">
                      {item.statePengiriman === 'SUDAH_DIKIRIM' ? (
                        <Text variant="body-sm">{item?.predikatKinerja}</Text>
                      ) : (
                        <Text variant="body-sm" color="disabled">
                          Belum difinialisasi
                        </Text>
                      )}
                    </TableRowItem>
                    <TableRowItem className="min-w-[120px] print:hidden" />
                  </TableRow>
                )
              })}
          </TableBody>
        </TablePelaksanaanKinerja>
      </section>
    </>
  )
}

export default PredikatKinerja
